import { supabase } from '../config/supabase';
import { NewHire } from './hireService';
import { progressStageService, ProgressStage } from './progressStageService';

export interface MoveHireRequest {
  hireId: string;
  targetProgress: number;
  reason?: string;
  movedBy?: string;
}

export interface MoveHireResponse {
  success: boolean;
  message: string;
  updatedHire?: NewHire;
  error?: string;
}

export const moveHireService = {
  /**
   * Move a hire to a different progress stage
   * Only owners and admins can perform this action
   */
  async moveHire(request: MoveHireRequest): Promise<MoveHireResponse> {
    try {
      console.log('Moving hire:', request);

      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role, full_name')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Verify user has appropriate role
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can move hires');
      }

      // Validate target progress
      if (request.targetProgress < 0 || request.targetProgress > 6) {
        throw new Error('Invalid target progress stage. Must be between 0 and 6.');
      }

      // Get the current hire data
      const { data: currentHire, error: fetchError } = await supabase
        .from('new_hire')
        .select('*')
        .eq('id', request.hireId)
        .single();

      if (fetchError || !currentHire) {
        throw new Error('Hire not found');
      }

      // Verify the hire belongs to the same organization
      if (currentHire.org_id !== profile.org_id) {
        throw new Error('Access denied: Hire does not belong to your organization');
      }

      // If user is admin, verify they have access to this hire
      if (profile.role === 'admin') {
        const recruiterEmails = currentHire.recruiter_emails || '';
        if (!recruiterEmails.toLowerCase().includes(user.email?.toLowerCase() || '')) {
          throw new Error('Access denied: You can only move hires assigned to you');
        }
      }

      // Prepare update data
      const updateData: Partial<NewHire> = {
        progress: request.targetProgress,
        update_timestamp: new Date().toISOString()
      };

      // Update checkListNum based on the target progress using automation rules
      // Try to get checklist number from automation rules first
      const { checklistAutomationService } = await import('./checklistAutomationService');
      const checkListNum = await checklistAutomationService.getCheckListNumForProgress(
        profile.org_id,
        request.targetProgress
      );
      
      if (checkListNum) {
        updateData.checkListNum = checkListNum;
      } else {
        // Fallback to hardcoded mapping if no automation rule exists
        const fallbackCheckListNum = this.getCheckListNumForProgress(request.targetProgress);
        if (fallbackCheckListNum) {
          updateData.checkListNum = fallbackCheckListNum;
        }
      }

      // Perform the update
      const { data: updatedHire, error: updateError } = await supabase
        .from('new_hire')
        .update(updateData)
        .eq('id', request.hireId)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      // Log the move action for audit purposes
      await this.logMoveAction({
        hireId: request.hireId,
        fromProgress: currentHire.progress,
        toProgress: request.targetProgress,
        movedBy: profile.full_name || user.email || 'Unknown',
        reason: request.reason,
        orgId: profile.org_id
      });

      console.log('Successfully moved hire:', {
        hireId: request.hireId,
        fromProgress: currentHire.progress,
        toProgress: request.targetProgress,
        movedBy: profile.full_name
      });

      return {
        success: true,
        message: `Successfully moved ${currentHire.fullName} from "${this.getStageLabel(currentHire.progress)}" to "${this.getStageLabel(request.targetProgress)}"`,
        updatedHire: {
          ...updatedHire,
          progress: Number(updatedHire.progress)
        }
      };

    } catch (error: any) {
      console.error('Error moving hire:', error);
      return {
        success: false,
        message: 'Failed to move hire',
        error: error.message
      };
    }
  },

  /**
   * Get all hires that can be moved by the current user
   */
  async getMovableHires(): Promise<NewHire[]> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can view movable hires');
      }

      let query = supabase
        .from('new_hire')
        .select('*')
        .eq('org_id', profile.org_id);

      // If admin, filter by recruiter_emails
      if (profile.role === 'admin') {
        query = query.ilike('recruiter_emails', `%${user.email}%`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data?.map(hire => ({
        ...hire,
        progress: Number(hire.progress)
      })) || [];

    } catch (error: any) {
      console.error('Error fetching movable hires:', error);
      throw error;
    }
  },

  /**
   * Get available target stages for a hire
   */
  async getAvailableTargetStages(currentProgress: number): Promise<ProgressStage[]> {
    const stages = await progressStageService.getCurrentUserProgressStages();
    return stages.filter(stage => stage.value !== currentProgress);
  },

  /**
   * Get stage label by progress value for current user's organization
   */
  async getStageLabel(progress: number): Promise<string> {
    const stages = await progressStageService.getCurrentUserProgressStages();
    const stage = stages.find(s => s.value === progress);
    return stage?.label || 'Unknown Stage';
  },

  /**
   * Get stage description by progress value for current user's organization
   */
  async getStageDescription(progress: number): Promise<string> {
    const stages = await progressStageService.getCurrentUserProgressStages();
    const stage = stages.find(s => s.value === progress);
    return stage?.description || 'No description available';
  },

  /**
   * Reverse mapping from progress to checkListNum based on OnboardingSteps.tsx algorithm
   */
  getCheckListNumForProgress(progress: number): string | null {
    return progressStageService.getCheckListNumForProgress(progress);
  },

  /**
   * Log move actions for audit purposes
   */
  async logMoveAction(logData: {
    hireId: string;
    fromProgress: number;
    toProgress: number;
    movedBy: string;
    reason?: string;
    orgId: string;
  }): Promise<void> {
    try {
      // You can implement this to log to a separate audit table
      // For now, we'll just console.log the action
      console.log('Move action logged:', {
        ...logData,
        timestamp: new Date().toISOString()
      });

      // TODO: Implement actual audit logging to database table
      // const { error } = await supabase
      //   .from('hire_move_audit')
      //   .insert([{
      //     hire_id: logData.hireId,
      //     from_progress: logData.fromProgress,
      //     to_progress: logData.toProgress,
      //     moved_by: logData.movedBy,
      //     reason: logData.reason,
      //     org_id: logData.orgId,
      //     timestamp: new Date().toISOString()
      //   }]);

      // if (error) {
      //   console.warn('Failed to log move action:', error);
      // }

    } catch (error) {
      console.warn('Error logging move action:', error);
    }
  },

  /**
   * Validate if a move is allowed
   */
  validateMove(currentProgress: number, targetProgress: number): { valid: boolean; message?: string } {
    return progressStageService.validateMove(currentProgress, targetProgress);
  },

  /**
   * Get move history for a hire (if audit logging is implemented)
   */
  async getMoveHistory(hireId: string): Promise<any[]> {
    try {
      // TODO: Implement when audit table is created
      // const { data, error } = await supabase
      //   .from('hire_move_audit')
      //   .select('*')
      //   .eq('hire_id', hireId)
      //   .order('timestamp', { ascending: false });

      // if (error) throw error;
      // return data || [];

      return []; // Placeholder until audit table is implemented
    } catch (error) {
      console.error('Error fetching move history:', error);
      return [];
    }
  }
};

export default moveHireService; 